import { TakeEffect, takeEvery } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { getCurrentOrganization } from '@/core/state/selectors';
import { KEY_GET_NEW_CONTENTS, API_GET_NEW_CONTENTS } from '../constants';
import { MOCK_RESPONSE_GET_NEW_CONTENTS } from '../mocks';
import {
  API_GET_NEW_CONTENTS_REQUEST_PATH_PARAMS,
  API_GET_NEW_CONTENTS_REQUEST_QUERY_PARAMS,
  API_GET_NEW_CONTENTS_PROPS,
} from '../types';

export type RequestGetNewContents = API_GET_NEW_CONTENTS_REQUEST_PATH_PARAMS &
  API_GET_NEW_CONTENTS_REQUEST_QUERY_PARAMS;
export type ResponseGetNewContents = API_GET_NEW_CONTENTS_PROPS['response']['body'];

export type CallParamsGetNewContents = Omit<RequestGetNewContents, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetNewContents = ResponseGetNewContents;

type CallErrorGetNewContents = {
  message: string;
  code: number;
  type: string;
  title: string;
  status: number;
};

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetNewContents, 'successSaga' | 'errorSaga'>;
};

type CallSelectorsGetNewContents = {
  data: CallResponseGetNewContents['data'];
  totalItems: CallResponseGetNewContents['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getNewContentsCall = createGeneralCall<
  CallParamsGetNewContents,
  CallResponseGetNewContents,
  CallErrorGetNewContents,
  CallSelectorsGetNewContents
>({
  id: KEY_GET_NEW_CONTENTS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_NEW_CONTENTS(org as unknown as string, payload);
    },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_NEW_CONTENTS : undefined,
  cancellable: true,
  takeEffect: takeEvery as unknown as TakeEffect,
  rsmOptions: {
    doubleRun: false,
  },
});
